import apiClient from '../api/apiClient';

// In-memory cache store: Map<url, { data: any, timestamp: number }>
const cacheStore = new Map();

// Active in-flight requests: Map<url, Promise<any>>
const inFlightRequests = new Map();

// Default Cache TTL: 3 minutes (180,000 ms)
const DEFAULT_TTL = 3 * 60 * 1000;

/**
 * Perform a cached, deduplicated GET request
 * @param {string} url - API URL to fetch
 * @param {object} [options]
 * @param {number} [options.ttl=180000] - Time to live in ms (default 3 mins)
 * @param {boolean} [options.force=false] - If true, bypasses cache and forces network fetch
 * @returns {Promise<any>}
 */
export const apiGet = async (url, options = {}) => {
  const ttl = options.ttl !== undefined ? options.ttl : DEFAULT_TTL;
  const force = options.force || false;
  const now = Date.now();

  // 1. If not forcing, check in-memory cache
  if (!force && cacheStore.has(url)) {
    const entry = cacheStore.get(url);
    if (now - entry.timestamp < ttl) {
      return entry.data;
    }
  }

  // 2. Request deduplication: if identical request is already in-flight, reuse promise
  if (inFlightRequests.has(url)) {
    return inFlightRequests.get(url);
  }

  // 3. Initiate request and track promise
  const fetchPromise = apiClient.get(url)
    .then((response) => {
      cacheStore.set(url, {
        data: response.data,
        timestamp: Date.now()
      });
      return response.data;
    })
    .catch((error) => {
      throw error;
    })
    .finally(() => {
      inFlightRequests.delete(url);
    });

  inFlightRequests.set(url, fetchPromise);
  return fetchPromise;
};

/**
 * Invalidate cached endpoints matching pattern (or all cache if omitted)
 * @param {string} [pattern] - substring or endpoint to invalidate, e.g. 'GetNotice'
 */
export const invalidateApiCache = (pattern) => {
  if (!pattern) {
    cacheStore.clear();
    return;
  }
  for (const key of cacheStore.keys()) {
    if (key.includes(pattern)) {
      cacheStore.delete(key);
    }
  }
};
